import * as React from 'react';
import { useEffect, useState, useReducer } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  Card,
  Container,
  Grid,
  InputAdornment,
  MenuItem,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { itIT } from '@mui/x-data-grid/locales';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
//--------------------------------------------------------
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
//--------------------------------------------------------
import { styled } from '@mui/system';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import {
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../../utils/Buttons';
import { CustomTextField } from '../../utils/StyledInputs';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { LoadingUI } from '../../utils/LayoutStyles';
import API from '../../api';
import { EntitiesDialog } from '../../utils/EntitiesDialog';

// Custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

export default function Users() {
  const navigate = useNavigate();
  const [currentProfile, setCurrentProfile] = useState('');
  const [apiCalled, setApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [firstSearchDone, setFirstSearchDone] = useState(false);

  const [cards, setCards] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  /* --------------------------------- DataGrid -------------------------------- */

  const [isDataLoading, setIsDataLoading] = useState(false);

  const columns = [
    { field: 'prefix', headerName: 'Anno Campagna', editable: false, hideable: false, flex: 0.5 },
    { field: 'name', headerName: 'Nome Utente', editable: false, hideable: true, flex: 0.5 },
    { field: 'surname', headerName: 'Cognome Utente', editable: false, hideable: true, flex: 0.5 },
    { field: 'fiscal_code', headerName: 'Codice Fiscale', editable: false, hideable: false, flex: 1 },
    { field: 'card_entity', headerName: 'Circolo di Tesseramento', editable: false, hideable: true, flex: 0.5 },
    { field: 'user_entity', headerName: 'Circolo di Riferimento', editable: false, hideable: true, flex: 0.5 },
    { field: 'card_number', headerName: 'Numero Tessera', editable: false, hideable: false, flex: 1 },
    {
      field: 'created_at',
      headerName: 'Creazione Tessera',
      editable: false,
      flex: 1,
      renderCell: (params) => (params.value ? dayjs(params.value).format('DD/MM/YYYY') : ''),
    },
    {
      field: 'active',
      headerName: 'Stato Tessera',
      editable: false,
      flex: 1,
      type: 'boolean',
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    { field: 'payment_type', headerName: 'Tipo di Pagamento', editable: false, hideable: true, flex: 0.5 },
    { field: 'payment_status', headerName: 'Stato di Pagamento', editable: false, hideable: true, flex: 0.5 },
    {
      field: 'position_rate_price',
      headerName: 'Importo per Carica Amministrativa',
      valueFormatter: (value) => (value ? `${value} €` : value),
      editable: false,
      hideable: false,
      flex: 1,
    },
    {
      field: 'price',
      headerName: 'Importo',
      valueFormatter: (value) => `${value} €`,
      editable: false,
      hideable: false,
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: false,
      renderCell: (params) => {
        return (
          <CustomIconButton
            onClick={() => navigate('/admin/users/user-details', { state: { userID: params.row.user_id } })}
          >
            <KeyboardArrowRightIcon />
          </CustomIconButton>
        );
      },
      width: 80,
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarExport csvOptions={{ allColumns: true }} printOptions={{ disableToolbarButton: true }} />
      </GridToolbarContainer>
    );
  }

  const CustomNoRowsOverlay = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Typography>
        {firstSearchDone ? 'Nessuna tessera disponibile' : 'Utilizza i filtri per cercare i tesseramenti'}
      </Typography>
    </Box>
  );

  /* ---------------------------------- APIS ---------------------------------- */

  useEffect(() => {
    getCampaigns();
  }, []);

  const getCampaigns = () => {
    if (campaigns.length === 0) {
      API.get('/campaigns')
        .then((response) => {
          setApiCalled(true);
          setCampaigns(response.data.data);
        })
        .catch((error) => {
          setApiCalled(true);
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  /* ---------------------------------- Form ---------------------------------- */

  const [formState, dispatchFormState] = useReducer(formReducer, {
    campaign: '',
    entity: null,
  });

  function formReducer(state, action) {
    switch (action.type) {
      case 'CHANGE_CAMPAIGN':
        return { ...state, campaign: action.value };
      case 'CHANGE_ENTITY':
        return { ...state, entity: action.value };
      case 'RESET_FILTERS':
        return {
          campaign: '',
          entity: null,
        };
      default:
        return state;
    }
  }

  const handleEntityChange = (value) => {
    dispatchFormState({ type: 'CHANGE_ENTITY', value });
  };

  const handleCampaignChange = (value) => {
    dispatchFormState({ type: 'CHANGE_CAMPAIGN', value });
  };

  const handleResetFilters = (e) => {
    e.preventDefault();

    dispatchFormState({ type: 'RESET_FILTERS' });
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    setFirstSearchDone(true);
    setIsDataLoading(true);

    if (formState.campaign !== '' || formState.entity !== null) {
      const params = new URLSearchParams();

      if (formState.campaign !== '') {
        params.append('campaign_id', formState.campaign);
      }

      if (formState.entity) {
        params.append('entity_id', formState.entity.id);
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';

      API.get(`/memcards${queryString}`)
        .then((response) => {
          setCards(response.data);
          setIsDataLoading(false);
        })
        .catch((error) => {
          setIsDataLoading(false);
          HttpErrorHandler(error, setErrorMessage);
        });
    } else {
      showPopup(false, 'Selezionare almeno un filtro di ricerca');
    }
  };

  /* ----------------------------- EntitiesDialog ----------------------------- */

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  /* ---------------------------------- Popup --------------------------------- */

  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');

  const showPopup = (success, text) => {
    const totalDelay = success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR;
    const animationDelay = 400;

    setPopupSuccess(success);
    setOpen(true);
    setPopUpText(text);

    setTimeout(() => {
      setOpen(false);

      setTimeout(() => {}, animationDelay);
    }, totalDelay);
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}>
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <>
      <Helmet>
        <title>Anagrafiche Utenti</title>
      </Helmet>

      <Container maxWidth="xl">
        {apiCalled ? (
          errorMessage?.length === 0 ? (
            <>
              <Card sx={{ padding: '24px', marginBottom: '12px' }}>
                <form onSubmit={handleSubmitForm}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4} sx={{ alignSelf: 'start' }}>
                      <CustomTextField
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                              {formState.entity ? (
                                <Tooltip title="Elimina" placement="top" arrow>
                                  <DeleteIcon
                                    onClick={() => {
                                      handleEntityChange(null);
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Modifica" placement="top" arrow>
                                  <EditIcon
                                    onClick={() => {
                                      setOpenEntitiesFilter(!openEntitiesFilter);
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        }}
                        value={formState.entity ? formState.entity?.name : ''}
                        label="Entità"
                      />

                      <EntitiesDialog
                        openDialog={openEntitiesFilter}
                        closeDialog={handleCloseEntitiesFilterDialog}
                        outputEntity={handleEntityChange}
                        context={'users'}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="campaign"
                        label="Campagna di Tesseramento"
                        select
                        value={formState.campaign}
                        onChange={(e) => handleCampaignChange(e.target.value)}
                      >
                        <MenuItem value="">Nessuna Campagna</MenuItem>
                        {campaigns.map((campaign) => (
                          <MenuItem key={campaign.id} value={campaign.id}>
                            {campaign.description}
                          </MenuItem>
                        ))}
                      </CustomTextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <DefaultOutlinedButton
                        onClick={handleResetFilters}
                        customStyles={{ width: '100%', height: '100%', backgroundColor: '#f4f6f8'  }}
                      >
                        Svuota Filtri
                      </DefaultOutlinedButton>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={2}>
                      <DefaultContainedButton
                        type="submit"
                        customStyles={{ width: '100%', height: '100%'}}
                      >
                        Cerca
                      </DefaultContainedButton>
                    </Grid>
                  </Grid>
                </form>
              </Card>
              <Card sx={{ padding: '24px' }}>
                <DataGrid
                  sx={{ minHeight: '200px' }}
                  disableRowSelectionOnClick
                  rows={cards}
                  columns={columns}
                  slots={{ toolbar: CustomToolbar, noRowsOverlay: CustomNoRowsOverlay }}
                  localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
                  pageSizeOptions={[10]}
                  loading={isDataLoading}
                  getRowId={(row) => row.prefix + row.card_number}
                  initialState={{
                    ...cards.initialState,
                    pagination: { paginationModel: { pageSize: 10 } },
                    columns: {
                      ...cards.initialState?.columns,
                      columnVisibilityModel: {
                        payment_type: false,
                        payment_status: false,
                        card_entity: false,
                        user_entity: false,
                        name: false,
                        surname: false,
                      },
                    },
                  }}
                />
                {displayPopup()}
              </Card>
            </>
          ) : (
            // Show error message
            ErrorUI(errorMessage)
          )
        ) : (
          // Show loading
          LoadingUI()
        )}
      </Container>
    </>
  );
}
